/* eslint-disable no-unused-vars */
import { useEffect, useRef } from "react";
import * as THREE from "three";
import Globe from "globe.gl";
import PropTypes from "prop-types";

const GlobeVisualization = ({
  data = [],
  width = 800,
  height = 600,
  backgroundColor = "#000000",
  rotationSpeed = 0.3,
  autoRotate = true,
  onCountryClick = () => {},
  style = {},
}) => {
  const globeRef = useRef();
  const globeInstance = useRef(null);
  const animationMix = useRef([]);

  useEffect(() => {
    if (!globeRef.current) return;

    // Clean previous globe if needed
    if (globeInstance.current) globeInstance.current._destructor();

    const tooltip = document.createElement("div");
    tooltip.id = "globe-tooltip";
    Object.assign(tooltip.style, {
      position: "absolute",
      color: "#fff",
      background: "#111",
      padding: "6px 10px",
      borderRadius: "5px",
      pointerEvents: "none",
      fontSize: "14px",
      zIndex: 999,
      display: "none",
    });
    globeRef.current.appendChild(tooltip);

    // Globe setup(INITIALIZATION)
    const globe = Globe()(globeRef.current)
      .globeImageUrl("//unpkg.com/three-globe/example/img/earth-dark.jpg")
      .bumpImageUrl("//unpkg.com/three-globe/example/img/earth-topology.png")
      .backgroundImageUrl("//unpkg.com/three-globe/example/img/night-sky.png")
      .width(width)
      .height(height)
      .backgroundColor(backgroundColor)
      .atmosphereColor("#ffffff")
      .atmosphereAltitude(0.1);

    const scene = globe.scene();

    // Clear default points
    globe.pointsData([]);
    // Force camera to look at Africa (e.g. Nairobi)
    setTimeout(() => {
      globeInstance.current.pointOfView(
        {
          lat: 0, // around equator
          lng: 20, // somewhere around Central Africa
          altitude: 2.2,
        },
        2000
      ); // duration in ms for smooth animation
    }, 1000); // wait a bit before triggering (after rendering)

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff, 0.8));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1);
    dirLight.position.set(1, 1, 1);
    scene.add(dirLight);

    // Lat/Lng to Vec3
    const latLngToVector3 = (lat, lng, radius = 101.5) => {
      const phi = (90 - lat) * (Math.PI / 180);
      const theta = (lng + 180) * (Math.PI / 180);
      const x = -radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.cos(phi);
      const z = radius * Math.sin(phi) * Math.sin(theta);
      return new THREE.Vector3(x, y, z);
    };

    const loader = new THREE.TextureLoader();

    const ROTATION_OFFSET = -92;

    const adjustedData = data.map((point) => ({
      ...point,
      lng: ((point.lng + ROTATION_OFFSET + 180) % 360) - 180,
    }));

    adjustedData.forEach(
      ({ lat, lng, color = "#00ffff", size = 1.5, flagUrl, city, country }) => {
        const pos = latLngToVector3(lat, lng);

        // Flag Sprite
        if (flagUrl) {
          loader.load(flagUrl, (texture) => {
            const material = new THREE.SpriteMaterial({ map: texture });
            const sprite = new THREE.Sprite(material);
            sprite.scale.set(10, 6, 1);
            sprite.position.copy(pos.clone().multiplyScalar(1.15));
            sprite.userData = { city, country };
            scene.add(sprite);
          });
        }

        // Glow
        const glow = new THREE.Mesh(
          new THREE.SphereGeometry(size, 32, 32),
          new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.95,
            depthWrite: false,
          })
        );
        glow.position.copy(pos);
        glow.userData = { city, country };
        scene.add(glow);

        // Ripple
        const ring = new THREE.Mesh(
          new THREE.RingGeometry(size * 1.2, size * 2, 64),
          new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.7,
            side: THREE.DoubleSide,
            depthWrite: false,
          })
        );
        ring.position.copy(pos.clone().multiplyScalar(1.01));
        ring.lookAt(new THREE.Vector3(0, 0, 0));
        scene.add(ring);

        animationMix.current.push({ ring, scale: 1, opacity: 0.7 });
      }
    );

    // Animate ripples
    const animate = () => {
      requestAnimationFrame(animate);
      animationMix.current.forEach((entry) => {
        entry.scale += 0.01;
        entry.opacity -= 0.005;

        if (entry.scale > 2 || entry.opacity <= 0) {
          entry.scale = 1;
          entry.opacity = 0.5;
        }

        entry.ring.scale.set(entry.scale, entry.scale, entry.scale);
        entry.ring.material.opacity = entry.opacity;
      });
      globe.renderer().render(scene, globe.camera());
    };
    animate();

    // Auto-rotate setup
    if (autoRotate) {
      const controls = globe.controls();
      controls.autoRotate = true;
      controls.autoRotateSpeed = rotationSpeed;
      controls.enableZoom = true;
      controls.enablePan = true;
      controls.minDistance = 180;
      controls.maxDistance = 300;
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
    }

    // Hover Interactions
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    globeRef.current.addEventListener("mousemove", (event) => {
      const bounds = globeRef.current.getBoundingClientRect();
      mouse.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
      mouse.y = -((event.clientY - bounds.top) / bounds.height) * 2 + 1;

      raycaster.setFromCamera(mouse, globe.camera());
      const intersects = raycaster.intersectObjects(scene.children, true);
      let found = false;

      for (const intersect of intersects) {
        const { city, country } = intersect.object.userData || {};
        if (city && country) {
          tooltip.style.display = "block";
          tooltip.innerHTML = `<b>${city}</b>, ${country}`;
          tooltip.style.left = `${event.clientX + 10}px`;
          tooltip.style.top = `${event.clientY + 10}px`;
          found = true;
          break;
        }
      }

      if (!found) tooltip.style.display = "none";
    });

    globeInstance.current = globe;

    return () => {
      if (globeInstance.current) globeInstance.current._destructor();
    };
  }, [data, width, height, backgroundColor, rotationSpeed, autoRotate]);

  return (
    <div
      ref={globeRef}
      style={{
        width: "100%",
        height: "100%",
        minHeight: "400px",
        position: "relative",
        background: "linear-gradient(to bottom, #000000, #1a1a1a)",
        ...style,
      }}
    />
  );
};

// PropTypes
GlobeVisualization.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      size: PropTypes.number,
      color: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.string,
      flagUrl: PropTypes.string,
    })
  ),
  width: PropTypes.number,
  height: PropTypes.number,
  backgroundColor: PropTypes.string,
  rotationSpeed: PropTypes.number,
  autoRotate: PropTypes.bool,
  onCountryClick: PropTypes.func,
  style: PropTypes.object,
};

export default GlobeVisualization;
